import { getSupabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'
import type {
  StudioDocumentsRegistryRow,
  ContractEditable,
  DocumentType,
  DocReadiness,
  ContractRenewalStatus,
  DocumentReviewStatus,
} from '@/types'

export interface ContractCreationPartnerOption {
  partner_number: number
  persons_fio: string | null
}

export interface ContractCreationCityOption {
  city_id: number
  city_name: string
  country: string
}

export interface ContractCreationOptions {
  partners: ContractCreationPartnerOption[]
  cities: ContractCreationCityOption[]
}

function formatError(error: unknown): string {
  if (!error) return 'Unknown error'
  if (error instanceof Error) return error.message
  if (typeof error === 'object') {
    const e = error as { message?: string; details?: string; hint?: string; code?: string }
    return [e.message, e.details, e.hint, e.code].filter(Boolean).join(' | ') || JSON.stringify(error)
  }
  return String(error)
}

function todayIsoLocal(): string {
  const d = new Date()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

function getContractStart(row: StudioDocumentsRegistryRow): string {
  return row.contract_effective_from ?? row.contract_start ?? row.contract_signed_at ?? ''
}

function sortDocumentsRegistryRows(rows: StudioDocumentsRegistryRow[]): StudioDocumentsRegistryRow[] {
  return [...rows].sort((a, b) => {
    const studioA = a.studio_number ?? Number.MAX_SAFE_INTEGER
    const studioB = b.studio_number ?? Number.MAX_SAFE_INTEGER
    if (studioA !== studioB) return studioA - studioB

    const startCompare = getContractStart(a).localeCompare(getContractStart(b))
    if (startCompare !== 0) return startCompare

    return a.contract_id - b.contract_id
  })
}

const AMENDMENT_LOOKUP_BATCH_SIZE = 200

async function enrichRowsWithTermExtensionFlag(
  rows: StudioDocumentsRegistryRow[]
): Promise<StudioDocumentsRegistryRow[]> {
  if (rows.length === 0) return rows

  const supabase = getSupabase()
  const extendedContractIds = new Set<number>()
  const contractIds = rows.map(row => row.contract_id)

  // The registry is larger than 1,000 rows. Keep both the URL generated by
  // PostgREST's `in` filter and every response comfortably below its limits.
  for (let offset = 0; offset < contractIds.length; offset += AMENDMENT_LOOKUP_BATCH_SIZE) {
    const batch = contractIds.slice(offset, offset + AMENDMENT_LOOKUP_BATCH_SIZE)
    const { data, error } = await withAuthRecovery(() =>
      (supabase as any)
        .from('contract_amendments')
        .select('contract_id')
        .in('contract_id', batch)
        .eq('amendment_type', 'TERM_EXTENSION')
        .not('activated_at', 'is', null)
    )
    if (error) throw new Error(formatError(error))
    for (const amendment of data ?? []) {
      extendedContractIds.add(Number(amendment.contract_id))
    }
  }

  return rows.map(row => ({
    ...row,
    has_term_extension_amendment: extendedContractIds.has(row.contract_id),
  }))
}

async function enrichRowsWithExplicitContractDates(
  rows: StudioDocumentsRegistryRow[]
): Promise<StudioDocumentsRegistryRow[]> {
  const today = todayIsoLocal()
  const needsDateLookup = rows.filter(row =>
    !Object.prototype.hasOwnProperty.call(row, 'contract_actual_end_date') &&
    !Object.prototype.hasOwnProperty.call(row, 'contract_expected_end_date') &&
    row.contract_end != null &&
    today > row.contract_end
  )

  if (needsDateLookup.length === 0) return rows

  const supabase = getSupabase()
  const details = await Promise.all(needsDateLookup.map(async row => {
    const { data, error } = await withAuthRecovery(() =>
      (supabase as any).rpc('get_contract_editable', { p_contract_id: row.contract_id })
    )
    if (error) throw new Error(formatError(error))
    const editable = ((data ?? []) as ContractEditable[])[0]
    return editable ? [row.contract_id, editable] as const : null
  }))
  const detailsById = new Map(
    details.filter((item): item is readonly [number, ContractEditable] => item != null)
  )

  return rows.map(row => {
    const editable = detailsById.get(row.contract_id)
    if (!editable) return row
    const actualEndDate =
      editable.actual_end_date ??
      (editable.billing_end_source === 'TERMINATION' ? editable.billing_end_date ?? null : null)
    const currentExpectedEndDate =
      editable.legal_current_expected_end_date ??
      (editable.billing_end_source === 'TERM_EXTENSION'
        ? editable.billing_end_date ?? editable.expected_end_date
        : editable.expected_end_date)
    return {
      ...row,
      contract_signed_at: editable.signed_at,
      contract_effective_from: editable.effective_from,
      contract_base_expected_end_date: editable.expected_end_date,
      contract_expected_end_date: currentExpectedEndDate,
      contract_actual_end_date: actualEndDate,
      legal_status_code: editable.legal_status_code ?? row.legal_status_code,
      legal_status_label: editable.legal_status_label ?? row.legal_status_label,
      legal_status_reason: editable.legal_status_reason ?? row.legal_status_reason,
      legal_end_basis: editable.legal_end_basis ?? row.legal_end_basis,
      legal_source_amendment_id: editable.legal_source_amendment_id ?? row.legal_source_amendment_id,
    }
  })
}

// На PROD включён PostgREST `max-rows = 1000`: он молча режет ответ любого
// запроса, включая RPC `returns table`. Реестр ДКК уже больше 1000 строк
// (1308 на 02.08.2026), поэтому один вызов физически не может отдать его
// целиком — читаем страницами по 1000 через p_limit/p_offset самой RPC.
const REGISTRY_PAGE_SIZE = 1000
const REGISTRY_MAX_PAGES = 20

export async function listDocumentsRegistry(params: {
  search?: string
  onlyUnverified?: boolean
  limit?: number
  offset?: number
} = {}): Promise<StudioDocumentsRegistryRow[]> {
  const supabase = getSupabase()
  const search = params.search?.trim() || null
  const onlyUnverified = params.onlyUnverified ?? false
  // limit не задан => тянем реестр целиком (постранично).
  const wanted = params.limit ?? Number.POSITIVE_INFINITY

  const collected: StudioDocumentsRegistryRow[] = []
  let offset = params.offset ?? 0

  for (let page = 0; page < REGISTRY_MAX_PAGES && collected.length < wanted; page += 1) {
    const pageSize = Math.min(REGISTRY_PAGE_SIZE, wanted - collected.length)
    const { data, error } = await withAuthRecovery(() =>
      (supabase as any).rpc('list_studio_documents_registry', {
        p_search: search,
        p_only_unverified: onlyUnverified,
        p_limit: pageSize,
        p_offset: offset,
      })
    )
    if (error) throw new Error(formatError(error))
    const rows = (data ?? []) as StudioDocumentsRegistryRow[]
    collected.push(...rows)
    if (rows.length < pageSize) break
    offset += rows.length
  }

  const withDates = await enrichRowsWithExplicitContractDates(collected)
  const enriched = await enrichRowsWithTermExtensionFlag(withDates)
  return sortDocumentsRegistryRows(enriched)
}

export async function getDocumentsRegistryRow(contractId: number): Promise<StudioDocumentsRegistryRow | null> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_studio_documents_registry_row', { p_contract_id: contractId })
  )
  if (error) throw new Error(formatError(error))
  const rows = (data ?? []) as StudioDocumentsRegistryRow[]
  const withDates = await enrichRowsWithExplicitContractDates(rows)
  const enriched = await enrichRowsWithTermExtensionFlag(withDates)
  return enriched[0] ?? null
}

export async function upsertStudioDocument(input: {
  contractId: number
  docType: DocumentType
  url?: string | null
  note?: string | null
  readiness?: DocReadiness | null
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('upsert_studio_document', {
      p_contract_id: input.contractId,
      p_doc_type: input.docType,
      p_url: input.url ?? null,
      p_note: input.note ?? null,
      p_readiness: input.readiness ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
  return data as number
}

export async function deleteStudioDocument(documentId: number): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('delete_studio_document', { p_document_id: documentId })
  )
  if (error) throw new Error(formatError(error))
}

export async function setStudioDocumentChecklist(input: {
  contractId: number
  requisitesFormDone?: boolean | null
  reviewStatus?: DocumentReviewStatus | null
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('set_studio_document_checklist', {
      p_contract_id: input.contractId,
      p_requisites_form_done: input.requisitesFormDone ?? null,
      p_review_status: input.reviewStatus ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function createContractRenewalFromContract(input: {
  sourceContractId: number
  contractNumber: string
  signedAt?: string | null
  effectiveFrom: string
  expectedEndDate?: string | null
  closeSource?: boolean
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('create_contract_renewal_from_contract', {
      p_source_contract_id: input.sourceContractId,
      p_contract_number: input.contractNumber.trim(),
      p_signed_at: input.signedAt || null,
      p_effective_from: input.effectiveFrom,
      p_expected_end_date: input.expectedEndDate || null,
      p_close_source: input.closeSource ?? true,
    })
  )
  if (error) throw new Error(formatError(error))
  return data as number
}

export async function loadContractCreationOptions(): Promise<ContractCreationOptions> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_contract_creation_options')
  )
  if (error) throw new Error(formatError(error))
  const row = Array.isArray(data) ? data[0] : data
  return {
    partners: (row?.partners ?? []) as ContractCreationPartnerOption[],
    cities: (row?.cities ?? []) as ContractCreationCityOption[],
  }
}

export async function createNewContractMasterData(input: {
  partnerNumber: number
  studioNumber: number
  cityId: number
  contractNumber: string
  signedAt?: string | null
  effectiveFrom: string
  expectedEndDate?: string | null
  studioAddress?: string | null
  entityId?: number | null
  seriesDescription?: string | null
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('create_new_contract_master_data', {
      p_partner_number: input.partnerNumber,
      p_studio_number: input.studioNumber,
      p_city_id: input.cityId,
      p_contract_number: input.contractNumber.trim(),
      p_signed_at: input.signedAt || null,
      p_effective_from: input.effectiveFrom,
      p_expected_end_date: input.expectedEndDate || null,
      p_studio_address: input.studioAddress?.trim() || null,
      p_entity_id: input.entityId ?? null,
      p_series_description: input.seriesDescription?.trim() || null,
    })
  )
  if (error) throw new Error(formatError(error))
  return data as number
}

// ---- Лейблы для UI ----

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  PARTNER_FOLDER: 'Папка франчайзи',
  PDKK: 'ПДКК',
  ASSURANCE_GK: 'Заверение ГК',
  NDA: 'НДА к ДКК',
  ROSPATENT_DS: 'ДС Роспатент',
  ODP_CONSENT: 'Согласие на ОПД',
  DKP_RESALE: 'ДКП (перепродажа)',
  DS_FOLDER: 'ДС (папка)',
  NOTIFICATIONS_FOLDER: 'Уведомления (папка)',
  TERMINATION: 'Расторжение',
  OTHER: 'Прочие документы',
}

// Порядок типов в карточке — как в исходном реестре.
export const DOCUMENT_TYPE_ORDER: DocumentType[] = [
  'PARTNER_FOLDER',
  'PDKK',
  'ASSURANCE_GK',
  'NDA',
  'ROSPATENT_DS',
  'ODP_CONSENT',
  'DKP_RESALE',
  'DS_FOLDER',
  'NOTIFICATIONS_FOLDER',
  'TERMINATION',
  'OTHER',
]

export const DOC_READINESS_LABELS: Record<DocReadiness, string> = {
  READY: 'Готово',
  NOT_READY: 'Не готово',
  VERIFIED: 'Проверено',
  NA: 'Н/Д',
}

export const RENEWAL_STATUS_LABELS: Record<ContractRenewalStatus, string> = {
  ACTIVE: 'Да',
  INACTIVE: 'Нет',
  RENEWAL_NOT_SIGNED: 'Не подписано продление',
}

export const REVIEW_STATUS_LABELS: Record<DocumentReviewStatus, string> = {
  READY: 'Готово',
  NOT_READY: 'Не готово',
  VERIFIED: 'Проверено',
}
